import { useEffect, useMemo, useState } from 'react';
import type { Service, Staff } from '@salonomy/types';
import { Button, Field, Modal, Select, TextArea, TextInput } from '@salonomy/ui';
import { useCreateAppointment } from '../../data/appointments.js';
import { useResolvedPrice, useServiceVariants } from '../../data/pricing.js';
import { serviceSpanMin } from '../../data/services.js';
import { money } from '../../lib/format.js';
import { useToast } from '../../lib/toast.js';
import { clockLabel, wallMinutesToUtcIso } from '../../lib/time.js';
import { MakeRecurringButton } from '../recurring/MakeRecurringButton.js';
import { bookingPriceDisplay } from './bookingPrice.js';

function parseTime(value: string): number | null {
  const m = /^([01]?\d|2[0-3]):([0-5]\d)$/.exec(value);
  if (!m) return null;
  return +m[1]! * 60 + +m[2]!;
}

/**
 * WS7: the staff a booking can be created for on this date, each with their adjusted working band (a
 * custom-hours exception) when present. Only members who are actually in are offered — an off / on
 * time-off member never appears here, so the diary can't book into a slot the salon won't take.
 */
export interface BookableChair {
  staff: Staff;
  band: { startMin: number; endMin: number } | null;
}

export function NewAppointmentModal({
  date,
  tz,
  chairs,
  services,
  onClose,
  defaults,
}: {
  date: string;
  tz: string;
  chairs: BookableChair[];
  services: Service[];
  onClose: () => void;
  defaults?: { staffId?: string; time?: string };
}) {
  const create = useCreateAppointment(date);
  const toast = useToast();
  const bookable = useMemo(() => services.filter((s) => s.active), [services]);

  const [staffId, setStaffId] = useState(defaults?.staffId ?? chairs[0]?.staff.id ?? '');
  const [serviceId, setServiceId] = useState(bookable[0]?.id ?? '');
  const [variantId, setVariantId] = useState('');
  const [clientName, setClientName] = useState('');
  const [time, setTime] = useState(defaults?.time ?? '10:00');
  const [notes, setNotes] = useState('');

  const service = bookable.find((s) => s.id === serviceId);

  // WS11: a service may price by variant (Cut → short/medium/long). When it does we let the operator
  // pick one — the chosen variant drives both the booking's DURATION (its chair time, via endAt) AND
  // its concrete price: the create path now sends variantId (committed 04ebc45), so the server resolves
  // and FREEZES the concrete priceQuoted. The resolved figure is therefore shown == charged and is the
  // headline (bookingPrice.ts), not an indicative floor. Only ACTIVE variants are bookable; a variant-
  // less service behaves exactly as before. We don't gate slot/time selection on any of this — it's
  // price + duration, not availability.
  const { data: variants } = useServiceVariants(serviceId || undefined);
  const activeVariants = useMemo(
    () => (variants ?? []).filter((v) => v.active).sort((a, b) => a.sortOrder - b.sortOrder),
    [variants],
  );
  // When the service changes, drop a stale variant choice and default to the first bookable variant
  // (so the resolved price reflects a concrete, charged combination rather than the bare service).
  useEffect(() => {
    setVariantId(activeVariants[0]?.id ?? '');
  }, [serviceId, activeVariants]);
  const variantId_ = activeVariants.some((v) => v.id === variantId) ? variantId : '';

  // The API resolves the concrete price for this (service, variant?, staff?) tuple. Its pricePence is
  // exactly what the create path commits (the server freezes it as priceQuoted once we send variantId),
  // so it is the shown == charged headline; its DURATION drives the booking span (a variant carries its
  // own chair time, honoured via endAt). Variant-less: resolved.durationMin == the service's own.
  const { data: resolved } = useResolvedPrice({
    serviceId: serviceId || undefined,
    variantId: variantId_ || undefined,
    staffId: staffId || undefined,
  });
  // The figures the surface may honestly show: the resolved concrete price (shown == charged) once it
  // has resolved, else the service.priceFrom floor shown as an indicative "from". No client money math.
  const priceDisplay = service
    ? bookingPriceDisplay(service, resolved, activeVariants.length > 0)
    : null;

  const startMin = parseTime(time);
  // The booking span mirrors serviceSpanMin (chair time + hands-off processing) but takes the chair
  // time from the RESOLVED duration, since a chosen variant carries its own. For a variant-less service
  // resolved.durationMin equals the service's own, so this is identical to today. The span is only
  // client geometry for the optimistic block + the overridable endAt — it is not money math.
  const spanMin =
    service != null ? (resolved?.durationMin ?? service.durationMin) + service.processingMin : null;
  const endMin = startMin != null && service && spanMin != null ? startMin + spanMin : null;
  // WS7: when the chosen member has a custom-hours band, the booking must fall fully inside it — the
  // diary won't offer a slot outside the hours they've declared they're in.
  const band = chairs.find((c) => c.staff.id === staffId)?.band ?? null;
  const outsideBand =
    band != null && startMin != null && endMin != null
      ? startMin < band.startMin || endMin > band.endMin
      : false;
  const valid =
    staffId && service && startMin != null && clientName.trim().length > 0 && !outsideBand;

  function submit() {
    if (!valid || !service || startMin == null || endMin == null) {
      toast.error(
        outsideBand && band
          ? `That time is outside ${clockLabel(band.startMin)}–${clockLabel(band.endMin)}.`
          : 'Add a client name, service and a valid time.',
      );
      return;
    }
    create.mutate({
      staffId,
      serviceId,
      // The chosen variant flows through: the server resolves + freezes the concrete priceQuoted from
      // it (shown == charged), and derives the chair time. Omitted for a variant-less service.
      ...(variantId_ ? { variantId: variantId_ } : {}),
      clientDisplayName: clientName.trim(),
      startAt: wallMinutesToUtcIso(date, startMin, tz),
      endAt: wallMinutesToUtcIso(date, endMin, tz),
      source: 'salon',
      depositPaid: 0,
      notes: notes.trim() || null,
    });
    onClose();
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="New appointment"
      width={460}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!valid}>
            Book it
          </Button>
        </>
      }
    >
      <Field label="Client">
        <TextInput
          autoFocus
          value={clientName}
          onChange={(e) => setClientName(e.target.value)}
          placeholder="Client name (or “Walk-in”)"
        />
      </Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Field label="With">
          <Select value={staffId} onChange={(e) => setStaffId(e.target.value)}>
            {chairs.map((c) => (
              <option key={c.staff.id} value={c.staff.id}>
                {c.staff.displayName}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Start"
          hint={band ? `In ${clockLabel(band.startMin)}–${clockLabel(band.endMin)}` : undefined}
        >
          <TextInput type="time" value={time} onChange={(e) => setTime(e.target.value)} />
        </Field>
      </div>
      <Field
        label="Service"
        hint={
          service
            ? activeVariants.length
              ? `from ${money(service.priceFrom)}`
              : `${money(service.priceFrom)} · ${serviceSpanMin(service)} min`
            : undefined
        }
      >
        <Select value={serviceId} onChange={(e) => setServiceId(e.target.value)}>
          {bookable.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
      </Field>

      {/* WS9: a priced-by-variant service (e.g. Cut → short/medium/long) gets a variant picker. The
          chosen variant sets the booking's chair time (its duration), which the create path honours
          via endAt; it does not set a concrete committed price (see below). Variant-less services
          don't render this row at all (unchanged flow). */}
      {activeVariants.length ? (
        <Field label="Length">
          <Select value={variantId_} onChange={(e) => setVariantId(e.target.value)}>
            {activeVariants.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </Select>
        </Field>
      ) : null}

      {/* The price shown before commitment is what the booking will be committed at (shown == charged).
          Once the API has resolved a concrete (service, variant?, stylist) price we show it exactly —
          the create path sends variantId and the server freezes that figure as priceQuoted. Only while
          no concrete price has resolved yet does it fall back to the "from £X" floor (service.priceFrom).
          We render the server's resolved.pricePence verbatim via money() — no client-side money math. */}
      {priceDisplay ? (
        <div
          className="mono"
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 12,
            fontSize: 13,
          }}
        >
          <span style={{ fontWeight: 600 }}>
            {priceDisplay.indicative
              ? `from ${money(priceDisplay.committedPricePence)}`
              : money(priceDisplay.committedPricePence)}
          </span>
          <span className="muted" style={{ fontSize: 12.5 }}>
            {priceDisplay.durationMin} min
            {priceDisplay.indicative ? ' · final price set at checkout' : ''}
          </span>
        </div>
      ) : null}

      {startMin != null && endMin != null ? (
        <div
          className={outsideBand ? undefined : 'muted'}
          style={{ fontSize: 12.5, color: outsideBand ? 'var(--slm-danger)' : undefined }}
        >
          {clockLabel(startMin)} – {clockLabel(endMin)}
          {outsideBand && band
            ? ` · outside working hours (${clockLabel(band.startMin)}–${clockLabel(band.endMin)})`
            : service?.requiresPatchTest
              ? ' · patch test required'
              : ''}
        </div>
      ) : null}

      {/* WS7: turn this one-off into a standing weekly/fortnightly booking. The series creates its own
          first occurrence (with conflict preview), so on success we close this modal rather than also
          firing the single-appointment create. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          margin: '6px 0 2px',
          paddingTop: 8,
          borderTop: '1px solid var(--slm-border)',
        }}
      >
        <span className="muted" style={{ fontSize: 12.5 }}>
          Repeats every week or two? Set the cadence once.
        </span>
        <MakeRecurringButton
          date={date}
          tz={tz}
          chairs={chairs.map((c) => c.staff)}
          services={bookable}
          current={{
            staffId,
            serviceId,
            time,
            clientDisplayName: clientName.trim() || undefined,
            clientId: null,
          }}
          onCreated={onClose}
        />
      </div>

      <Field label="Notes">
        <TextArea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional" />
      </Field>
    </Modal>
  );
}
